import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Send,
    Check,
    X as XIcon,
    Download,
    Briefcase,
    Mail,
    Copy,
    ExternalLink,
} from "lucide-react";
import { api, apiErrorMessage, API_BASE } from "@/lib/api";
import { StatusBadge, money, fmtDate, fmtDateTime } from "@/lib/format";
import GapReportBanner from "@/components/GapReportBanner";
import { ConfidenceBadge, SourceBadge } from "@/components/BidIntakeBadges";
import AmbiguityResolver, { ResolvedPill } from "@/components/AmbiguityResolver";

export default function EstimateDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/estimates/${id}`);
            setData(data.estimate);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const act = async (path, label) => {
        setBusy(true);
        setErr("");
        try {
            await api.post(`/estimates/${id}/${path}`);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e, `${label} failed`));
        } finally {
            setBusy(false);
        }
    };

    const convertToJob = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data: res } = await api.post(`/jobs`, { estimate_id: id });
            navigate(`/jobs/${res.job.id}`);
        } catch (e) {
            setErr(apiErrorMessage(e, "Convert failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div className="page-shell">
                {err ? (
                    <div
                        className="brand-card"
                        style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    >
                        {err}
                    </div>
                ) : (
                    <p className="text-muted">Loading estimate…</p>
                )}
            </div>
        );
    }

    const status = data.status;
    const canSend = status === "draft" || status === "sent" || status === "viewed";
    const canAcceptReject = ["draft", "sent", "viewed"].includes(status);
    const canConvert = status === "accepted" && !data.job_id;
    const publicUrl = data.public_url_path
        ? `${window.location.origin}${data.public_url_path}`
        : null;

    const copyPublic = async () => {
        if (!publicUrl) return;
        await navigator.clipboard.writeText(publicUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="page-shell">
            <Link
                to="/estimates"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="ed-back"
            >
                <ArrowLeft size={14} />
                Back to estimates
            </Link>

            <header className="fade-up mt-4 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="eyebrow">Estimate</p>
                    <h1 className="h1 mt-3" data-testid="ed-number">
                        {data.estimate_number}
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                        <StatusBadge status={data.status} testId="ed-status" />
                        <span className="text-muted text-sm">
                            Issued {fmtDate(data.created_at)} · Valid until{" "}
                            {fmtDate(data.expiration_date)}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <a
                        href={`${API_BASE}/estimates/${id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-secondary"
                        data-testid="ed-pdf"
                    >
                        <Download size={14} />
                        Download PDF
                    </a>
                    {canSend && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => act("send", "Send")}
                            disabled={busy || !data.client?.email}
                            title={
                                data.client?.email
                                    ? "Send to client (email is MOCKED in dev)"
                                    : "Add a client email to enable sending"
                            }
                            data-testid="ed-send"
                        >
                            <Send size={14} />
                            {status === "draft" ? "Send to client" : "Resend"}
                        </button>
                    )}
                    {canAcceptReject && (
                        <>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => act("accept", "Accept")}
                                disabled={busy}
                                data-testid="ed-accept"
                            >
                                <Check size={14} />
                                Mark accepted
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => act("reject", "Reject")}
                                disabled={busy}
                                data-testid="ed-reject"
                            >
                                <XIcon size={14} />
                                Reject
                            </button>
                        </>
                    )}
                    {canConvert && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={convertToJob}
                            disabled={busy}
                            data-testid="ed-convert"
                        >
                            <Briefcase size={14} />
                            Convert to job
                        </button>
                    )}
                    {data.job_id && (
                        <Link
                            to={`/jobs/${data.job_id}`}
                            className="btn btn-primary"
                            data-testid="ed-view-job"
                        >
                            <Briefcase size={14} />
                            Open job
                        </Link>
                    )}
                </div>
            </header>

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="ed-error"
                >
                    {err}
                </div>
            )}

            {/* Public share URL */}
            {publicUrl && (
                <div
                    className="brand-card mt-5 flex items-center justify-between gap-3 flex-wrap"
                    style={{ background: "var(--brand-cream)" }}
                    data-testid="ed-public-link"
                >
                    <div className="min-w-0">
                        <p className="eyebrow">Client view URL</p>
                        <p className="text-sm mt-1 break-all">
                            <Link
                                to={data.public_url_path}
                                target="_blank"
                                className="link-red"
                                data-testid="ed-public-link-anchor"
                            >
                                {publicUrl}
                            </Link>
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={copyPublic}
                            data-testid="ed-copy-public"
                        >
                            <Copy size={14} />
                            {copied ? "Copied" : "Copy link"}
                        </button>
                        <Link
                            to={data.public_url_path}
                            target="_blank"
                            className="btn btn-ghost"
                            data-testid="ed-open-public"
                        >
                            <ExternalLink size={14} />
                            Open
                        </Link>
                    </div>
                </div>
            )}

            {data.source === "bid_intake" && (
                <div className="mt-5">
                    <GapReportBanner
                        gapReport={data.gap_report}
                        jobId={data.bid_intake_job_id}
                    />
                </div>
            )}

            {/* Content grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <div className="brand-card fade-up">
                        <p className="eyebrow">Client</p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-muted">Name</p>
                                <p className="font-display font-bold">{data.client?.name}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted">Service</p>
                                <p className="font-display font-bold capitalize">
                                    {data.service_type}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted">Email</p>
                                {data.client?.email ? (
                                    <a
                                        href={`mailto:${data.client.email}`}
                                        className="link-red font-semibold"
                                    >
                                        {data.client.email}
                                    </a>
                                ) : (
                                    <p className="text-muted">—</p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs text-muted">Phone</p>
                                <p>{data.client?.phone || "—"}</p>
                            </div>
                            <div className="sm:col-span-2">
                                <p className="text-xs text-muted">Job address</p>
                                <p>{data.job_address}</p>
                            </div>
                        </div>
                    </div>

                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Line items</p>
                        <div className="overflow-x-auto mt-3">
                            <table className="brand-table" data-testid="ed-items">
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th className="text-right">Qty</th>
                                        <th>Unit</th>
                                        <th className="text-right">Price</th>
                                        <th className="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.line_items?.map((li, i) => {
                                        const meta = li.meta || null;
                                        const conf = meta?.auto_generated
                                            ? meta?.confidence || "medium"
                                            : "manual";
                                        const hasUnresolvedAmbiguity =
                                            (meta?.ambiguous_options || []).length > 0;
                                        const resolvedChoice = meta?.resolved_choice;
                                        return (
                                            <tr key={i} style={{ cursor: "default" }}>
                                                <td>
                                                    <span className="inline-flex items-center flex-wrap gap-1">
                                                        {li.description}
                                                        {meta?.auto_generated && (
                                                            <SourceBadge
                                                                source={meta.source}
                                                                ambiguous={meta.ambiguous_options}
                                                            />
                                                        )}
                                                        {resolvedChoice && (
                                                            <ResolvedPill
                                                                choice={resolvedChoice}
                                                            />
                                                        )}
                                                    </span>
                                                    <span className="text-xs text-muted ml-2 capitalize">
                                                        · {li.kind}
                                                    </span>
                                                    <span className="ml-2 inline-block align-middle">
                                                        <ConfidenceBadge confidence={conf} />
                                                    </span>
                                                    {hasUnresolvedAmbiguity && (
                                                        <AmbiguityResolver
                                                            lineItem={li}
                                                            index={i}
                                                            estimateId={data.id}
                                                            bidIntakeJobId={
                                                                data.bid_intake_job_id
                                                            }
                                                            onResolved={(updated) =>
                                                                setData(updated)
                                                            }
                                                        />
                                                    )}
                                                </td>
                                                <td className="text-right font-mono">
                                                    {li.quantity}
                                                </td>
                                                <td>{li.unit?.replace("_", " ")}</td>
                                                <td className="text-right font-mono">
                                                    {money(li.unit_price)}
                                                </td>
                                                <td className="text-right font-mono">
                                                    {money(li.total)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <div className="min-w-[260px] space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted">Labor</span>
                                    <span className="font-mono">{money(data.labor_total)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted">Materials</span>
                                    <span className="font-mono">
                                        {money(data.materials_total)}
                                    </span>
                                </div>
                                <div
                                    className="flex justify-between pt-2 border-t border-[var(--brand-surface)]"
                                    style={{ fontSize: "1.25rem" }}
                                >
                                    <span className="font-display font-bold">Grand total</span>
                                    <span
                                        className="font-display font-bold font-mono"
                                        style={{ color: "var(--brand-red)" }}
                                        data-testid="ed-grand"
                                    >
                                        {money(data.grand_total)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {data.notes && (
                        <div className="brand-card fade-up delay-2">
                            <p className="eyebrow">Notes</p>
                            <p className="mt-2 whitespace-pre-wrap">{data.notes}</p>
                        </div>
                    )}
                </div>

                {/* Timeline */}
                <div className="brand-card fade-up delay-1 self-start" data-testid="ed-timeline">
                    <p className="eyebrow">Timeline</p>
                    <ol className="mt-4 flex flex-col gap-3 text-sm">
                        <Step
                            label="Created"
                            time={data.created_at}
                            done
                            sub={data.created_by_name}
                        />
                        <Step
                            label="Sent to client"
                            time={data.sent_at}
                            done={!!data.sent_at}
                        />
                        <Step
                            label="Viewed by client"
                            time={data.viewed_at}
                            done={!!data.viewed_at}
                        />
                        <Step
                            label={
                                data.status === "rejected"
                                    ? "Rejected"
                                    : data.status === "accepted"
                                      ? "Accepted"
                                      : "Decision"
                            }
                            time={data.responded_at}
                            done={!!data.responded_at}
                            tone={data.status === "accepted" ? "success" : data.status === "rejected" ? "error" : undefined}
                        />
                    </ol>
                    <div className="mt-5 border-t border-[var(--brand-surface)] pt-4 text-xs text-muted flex items-start gap-2">
                        <Mail size={14} className="mt-0.5 shrink-0" />
                        <span>
                            Postmark is not wired yet — sending logs a mocked email entry on the
                            backend and updates status here.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Step({ label, time, done, sub, tone }) {
    const color = !done
        ? "var(--brand-surface-2)"
        : tone === "error"
          ? "var(--brand-red)"
          : tone === "success"
            ? "var(--brand-success)"
            : "var(--brand-red)";
    return (
        <li className="flex items-start gap-3">
            <span
                className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: color }}
            />
            <div className="min-w-0">
                <p className="font-display font-bold">{label}</p>
                <p className="text-xs text-muted">
                    {done ? fmtDateTime(time) : "Pending"}
                    {sub ? ` · ${sub}` : ""}
                </p>
            </div>
        </li>
    );
}
